// config/routeConfig.ts

export interface RouteConfig {
  path: string;
  label: string;
  icon?: string;
  component?: string;
  children?: Record<string, RouteConfig>;
  dynamicLabel?: (context: Record<string, any>) => string;
  hideInSidebar?: boolean;
}

export const ROUTE_CONFIG: Record<string, RouteConfig> = {
  dashboard: {
    path: '/',
    label: 'Dashboard',
    icon: 'pi-home',
    component: 'Dashboard',
  },


  Attendance: {
  path: '/attendance',
  label: 'Attendance',
  icon: 'pi-calendar',
  component: 'Attendance',
},

  Assignments: {
    path: '/assignments',
    label: 'Assignments',
    icon: 'pi-file-edit',
    component: 'Assignments',
    children: {
      list: {
        path: '/assignments',
        label: 'Assignments',
        component: 'Assignments',
        hideInSidebar: true,
      },
      ViewAssignment: {
        path: '/assignments/:id',
        label: 'View Assignment',
        component: 'ViewAssignment',
        hideInSidebar: true,
      },
    },
  },

  noticeboard: {
    path: '/noticeboard',
    label: 'NoticeBoard',
    icon: 'pi-bell',
    component: 'NoticeBoard'
  },

  Announcements: {
  path: '/announcements',
  label: 'Announcements',
  icon: 'pi-megaphone',
  component: 'AnnouncementsList',
},

  Exams: {
    path: '/exams',
    label: 'Exams',
    icon: 'pi-users',
    component: 'Exams',
    children: {
      list: {
        path: '/exams',
        label: 'Exams',
        component: 'Exams',
        hideInSidebar: true,
      },
      ClassTestList: {
        path: '/exams/class-tests',
        label: 'Class Tests',
        component: 'ClassTestList',
        hideInSidebar: false,
      },
      CreateClassTest: {
        path: '/exams/class-tests/create',
        label: 'Create Class Test',
        component: 'CreateClassTest',
        hideInSidebar: true,
      },
      ViewClassTest: {
        path: '/exams/class-tests/:id',
        label: 'View Class Test',
        component: 'ViewClassTest',
        hideInSidebar: true,
      },
      EditClassTest: {
        path: '/exams/class-tests/:id/edit',
        label: 'Edit Class Test',
        component: 'EditClassTest',
        hideInSidebar: true,
      },
      TakeExam: {
        path: '/exams/class-tests/:id/take',
        label: 'Take Exam',
        component: 'TakeExam',
        hideInSidebar: true,
      },
    },
  },


    PracticalMaterials: {
    path: '/practical-materials',
    label: 'Practical Materials',
    icon: 'pi-briefcase',
    component: 'PracticalMaterials',
  },


    StudentProfile: {
    path: '/profile',
    label: 'Profile',
    icon: 'pi-user-plus',
    component: 'StudentProfile',
  },
};

// ─── Helper types ────────────────────────────────────────────────────

export interface BreadcrumbItem {
  id: string;
  label: string;
  path: string;
}

// ─── Flatten routes ───────────────────────────────────────────────────────────

const flattenRoutes = (
  config: Record<string, RouteConfig> = ROUTE_CONFIG,
  parentId?: string
): Array<{ id: string; route: RouteConfig; parent?: string }> => {
  const flattened: Array<{ id: string; route: RouteConfig; parent?: string }> = [];

  for (const [id, route] of Object.entries(config)) {
    flattened.push({ id, route, parent: parentId });
    if (route.children) {
      flattened.push(...flattenRoutes(route.children, id));
    }
  }

  return flattened;
};

const FLATTENED_ROUTES = flattenRoutes();

// ─── findRouteByPath ──────────────────────────────────────────────────────────

export const findRouteByPath = (
  path: string
): { id: string; route: RouteConfig; parent?: string } | null => {
  // Longer paths first so specific routes beat dynamic ones
  const sortedRoutes = [...FLATTENED_ROUTES].sort(
    (a, b) => b.route.path.length - a.route.path.length
  );

  for (const { id, route, parent } of sortedRoutes) {
    if (route.path === path) return { id, route, parent };

    const pathPattern = route.path.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${pathPattern}$`);
    if (regex.test(path)) return { id, route, parent };
  }

  return null;
};

// ─── findRouteById ────────────────────────────────────────────────────────────

export const findRouteById = (
  id: string,
  config: Record<string, RouteConfig> = ROUTE_CONFIG,
  parentId?: string
): { route: RouteConfig; parent?: string } | null => {
  if (config[id]) return { route: config[id], parent: parentId };

  for (const [currentId, route] of Object.entries(config)) {
    if (route.children) {
      const result = findRouteById(id, route.children, currentId);
      if (result) return result;
    }
  }

  return null;
};

// ─── buildBreadcrumbTrail ─────────────────────────────────────────────────────

export const buildBreadcrumbTrail = (
  currentPath: string,
  context: Record<string, any> = {}
): BreadcrumbItem[] => {
  const trail: BreadcrumbItem[] = [];

  trail.push({ id: 'dashboard', label: 'Dashboard', path: '/' });

  const current = findRouteByPath(currentPath);
  if (!current || current.id === 'dashboard') return trail;

  const buildChain = (parentId?: string): string[] => {
    if (!parentId) return [];
    const parent = findRouteById(parentId);
    if (!parent) return [];
    const chain = buildChain(parent.parent);
    chain.push(parentId);
    return chain;
  };

  for (const parentId of buildChain(current.parent)) {
    const parentResult = findRouteById(parentId);
    if (parentResult) {
      trail.push({
        id: parentId,
        label: parentResult.route.dynamicLabel
          ? parentResult.route.dynamicLabel(context)
          : parentResult.route.label,
        path: parentResult.route.path,
      });
    }
  }

  trail.push({
    id: current.id,
    label: current.route.dynamicLabel
      ? current.route.dynamicLabel(context)
      : current.route.label,
    path: current.route.path,
  });

  return trail;
};

// ─── getSidebarRoutes ─────────────────────────────────────────────────────────

export const getSidebarRoutes = (
  config: Record<string, RouteConfig> = ROUTE_CONFIG
): Array<{ id: string; route: RouteConfig; children?: Array<{ id: string; route: RouteConfig }> }> => {
  const routes: Array<{
    id: string;
    route: RouteConfig;
    children?: Array<{ id: string; route: RouteConfig }>;
  }> = [];

  for (const [id, route] of Object.entries(config)) {
    if (route.hideInSidebar) continue;

    const item: any = { id, route };

    if (route.children) {
      item.children = [];
      for (const [childId, childRoute] of Object.entries(route.children)) {
        if (!childRoute.hideInSidebar) {
          item.children.push({ id: childId, route: childRoute });
        }
      }
    }

    routes.push(item);
  }

  return routes;
};

// ─── isRouteActive ────────────────────────────────────────────────────────────

export const isRouteActive = (routeId: string, currentPath: string): boolean => {
  const current = findRouteByPath(currentPath);
  if (!current) return false;
  if (current.id === routeId) return true;

  const buildParentChain = (parentId?: string): string[] => {
    if (!parentId) return [];
    const parent = findRouteById(parentId);
    if (!parent) return [];
    return [...buildParentChain(parent.parent), parentId];
  };

  return buildParentChain(current.parent).includes(routeId);
};

// ─── getPathForRoute ──────────────────────────────────────────────────────────

export const getPathForRoute = (
  routeId: string,
  params: Record<string, string> = {}
): string => {
  const result = findRouteById(routeId);
  if (!result) return '/';

  let path = result.route.path;
  for (const [key, value] of Object.entries(params)) {
    path = path.replace(`:${key}`, value);
  }

  return path;
};